import { useState, useEffect, useContext } from 'react'
import { Layout } from "../../componentsV1/layout";
import Logo from "../../assets/v1/logo.png";
import { Multiplexing, ReducedBackground, Graph, Target } from "../../assets/v1/icons";
import css from './product.module.scss'
import { AuthContext } from '../../context/auth'
import { getProductNIRApi, getProductFromListApi, getProductCategorysByIdApi } from '../../api'
import { useNavigate } from 'react-router-dom'
import MethodologyModal from "../../componentsV1/modals/MethodologyModal"
import { ProductList } from '../../componentsV1/ProductList'
import { ProductDetails } from '../../componentsV1/ProductDetails';
import productImg from '../../assets/v1/nir/productImg.png'
import { ExpertiseBg, WorkflowBg, ChemistryBg, StepdownBg } from '../../assets/v1/nir/background'

export const NIR = () => {
    const { setIsLoading, setNotification, setCartData, isLoading, setUpcomingPurchaseModal, setBulkOrderModal, tempDisable, setTempDisable, width } = useContext(AuthContext)
    const navigate = useNavigate()
    const [products, setProducts] = useState({
        primaryConjugates: [],
        secondary: []
    })
    const [openMethodologyModal, setOpenMethodologyModal] = useState(false)
    const [productId, setProductId] = useState(null)
    const [selectedProduct, setSelectedProduct] = useState(null)
    const [modalName, setModalName] = useState(null)
    const [activeTab, setActiveTab] = useState('primaryConjugates')

    const tabs = [
        {
            label: 'Conjugated Primary Antibodies',
            value: 'primaryConjugates'
        },
        {
            label: 'Secondary Fluorescent Antibodies',
            value: 'secondary'
        }
    ]

    const getProducts = async () => {
        setIsLoading(true)
        try {
            getProductNIRApi().then(res => {
                if (res && res.data) {
                    const primaryConjugates = res?.data?.products?.filter(item => item?.mid_code === 'DC')
                    const secondary = res?.data?.products?.filter(item => item?.mid_code === 'SA')
                    setProducts({
                        primaryConjugates,
                        secondary
                    })
                }
            }).catch(error => {
                console.log(error)
            }).finally(() => {
                setIsLoading(false)
            })
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        getProducts()
    }, [])

    const getProductByIdApi = async (id) => {
        try {
            getProductFromListApi(productId?.id).then(res => {
                if (res && res.data) {
                    const product = res?.data?.products[0]
                    setSelectedProduct(product)
                }
            })
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        if (productId) {
            getProductByIdApi()
        }
    }, [productId])

    const logos = [
        {
            icon: ReducedBackground,
            text: "Reduced Background"
        },
        {
            icon: Target,
            text: "Enhanced Sensitivity"
        },
        {
            icon: Multiplexing,
            text: "Advanced Multiplexing"
        },
        {
            icon: Graph,
            text: "Transformative Impact"
        }
    ]

    return (
        <Layout>
            <div className={css.heroContainer}>
                <div className={css.heroContent}>
                    <img src={Logo} alt="logo" className={css.heroLogo} />
                    <div className={css.heroText}>
                        <h4>Clarity Beyond The Visible</h4>
                        <div className={css.line} />
                        <h1>Expand the Optical Space <br /> <span>NIR Products</span></h1>
                    </div>
                    <div className={css.logoContainer}>
                        {logos.map((item) => {
                            return (
                                <div className={css.logo}>
                                    <img src={item.icon} alt={item.text} />
                                    <p>{item.text}</p>
                                </div>
                            )
                        })
                        }
                    </div>
                </div>
            </div>
            <div className={css.container}>
                <div className={`${css.twoColumn} ${css.smallContainer}`}>
                    <div className={css.column}>
                        <h2 className={css.heading}>Why Researchers Choose NIR</h2>
                        <p>As biological systems become increasingly complex, conventional fluorescence channels often struggle with background signal, spectral overlap, and limited multiplexing capacity.</p>
                        <p>Near-infrared (NIR) fluorophores expand the available optical space, enabling cleaner signal separation, improved sensitivity, and more confident interpretation of complex biological data.</p>
                        <p>For applications ranging from advanced imaging and translational biology to high-content screening and assay development, NIR technologies help researchers see more, measure more, and trust more.</p>
                    </div>
                    <div className={css.column}>
                        <div className={`${css.column} ${css.noBgCard}`}>
                            <img src={productImg} alt='DC000055' height={280} />
                            <div className={`${css.card} ${css.column} ${css.smallPadding}`}>
                                <p>Rabbit anti-Human γH2Ax (Serine 139) Recombinant Monoclonal - Identifyn® SRM Alexa Fluor™ 790, IgG - (BLR014M)</p>
                                <div className={css.divider} />
                                <p>Cat ID# - DC 000055</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={`${css.column} ${css.smallContainer}`}>
                    <h2 className={css.heading}>Why Identifyn NIR Products?</h2>
                    <p>Identifyn NIR products are developed within a scientific ecosystem that integrates advanced conjugation chemistry, microscopy expertise, super-resolution imaging, and the Stepdown™ validation framework.
                        The result is a portfolio designed not only to generate signal—but to generate confidence.</p>
                    <div className={css.fourColumn}>
                        <div className={`${css.card} ${css.column} ${css.cardBg}`} style={{ "--product-bg": `url(${StepdownBg})`}}>
                            <h5 className={css.specialText}>Stepdown™ Validated</h5>
                            <p>Validated beyond traditional catalog standards.</p>
                        </div>
                        <div className={`${css.card} ${css.column} ${css.cardBg}`} style={{ "--product-bg": `url(${ChemistryBg})`}}>
                            <h5 className={css.specialText}>Advanced Conjugation Chemistry</h5>
                            <p>Designed for performance, reproducibility, and application flexibility.</p>
                        </div>
                        <div className={`${css.card} ${css.column} ${css.cardBg}`} style={{ "--product-bg": `url(${ExpertiseBg})`}}>
                            <h5 className={css.specialText}>Imaging Expertise</h5>
                            <p>Built by scientists who generate and interpret the data.</p>
                        </div>
                        <div className={`${css.card} ${css.column} ${css.cardBg}`} style={{ "--product-bg": `url(${WorkflowBg})`}}>
                            <h5 className={css.specialText}>Complete Workflow Integration</h5>
                            <p>Designed to support reagent development, imaging, validation, and discovery.</p>
                        </div>
                    </div>
                </div>
            </div>
            <div className={`${css.section} ${selectedProduct ? css.showProduct : css.hideProduct}`}>
                <div className={css.selectionContainer}>
                    <div className={css.listContainer}>
                        <div className={css.filtersContainer}>
                            {tabs.map((item) => {
                                return <button key={item.value} onClick={() => {
                                    setActiveTab(item.value)
                                }}
                                    className={`${css.tab} ${activeTab === item.value ? css.activeTab : ''}`}
                                >{item.label}</button>
                            })
                            }
                        </div>
                        <div className={css.divider} />
                        <ProductList products={products[activeTab]} setSelectedProduct={setProductId} maxHeight={width > 750 ? '862px' : '500px'} />
                    </div>
                </div>
                <div className={css.productContainer}>
                    <ProductDetails selectedProduct={selectedProduct} setSelectedProduct={setSelectedProduct} setProductId={setProductId} setModalName={setModalName} setOpenMethodologyModal={setOpenMethodologyModal} />
                </div>
            </div>
            <div className={`${css.column} ${css.centerText} ${css.smallContainer} ${css.card} ${css.shadowCard}`}>
                <h2 className={css.heading}>NIR Reagents Built for Advanced Imaging</h2>
                <p>Identifyn® NIR products are developed using advanced conjugation chemistry and validated within a scientific imaging ecosystem spanning widefield, confocal, Airyscan, SIM, SIM², and single-molecule workflows. The result is a portfolio designed to deliver cleaner signal, greater flexibility, and more actionable data.</p>
            </div>
            {openMethodologyModal && <MethodologyModal setOpenMethodologyModal={setOpenMethodologyModal} selectedProduct={selectedProduct} modalName={modalName} setModalName={setModalName} />}
        </Layout>
    )
}